import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  TransactionAML,
  Response,
  TransactionWarning,
  BlacklistData,
  GeneralLedgerTransaction,
  TransactionWarningTypeEnum,
} from '@ui/src';
import { internalTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { CofProps, WarningsInfo } from '../../../transactions/type';
import { transactionApi } from '../../../transactions/store/services';

export interface InternalTransactionState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: BaseTransaction;
  aml?: TransactionAML;
  warnings?: TransactionWarning[];
  blacklistDataVisible: boolean;
  blacklistData?: BlacklistData;
  applyTransactionsWarningIsLoading?: boolean;
  senderCompanyOfficials?: CofProps[];
  recipientCompanyOfficials?: CofProps[];
  glTransactions?: GeneralLedgerTransaction[];
  warningsInfo?: WarningsInfo;
}

const initialState: InternalTransactionState = {
  panelIsOpen: true,
  blacklistDataVisible: false,
  applyTransactionsWarningIsLoading: false,
};

export const internalTransactionSlice = createSlice({
  name: 'internalTransaction',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.transactionId && action.payload.state) {
        state.transactionId = action.payload.transactionId;
      }
    },
    setWarnings: (state, action: PayloadAction<TransactionWarning[]>) => {
      state.warnings = action.payload;
    },
    setApplyTransactionsWarningIsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.applyTransactionsWarningIsLoading = action.payload;
    },
    setBlacklistDataVisible: (state, action: PayloadAction<boolean>) => {
      state.blacklistDataVisible = action.payload;
    },
    setBlacklistData: (
      state,
      action: PayloadAction<BlacklistData | undefined>,
    ) => {
      state.blacklistData = action.payload;
    },
    clearInternalTransactionState: (state) => {
      state.transactionId = undefined;
      state.transaction = undefined;
      state.panelIsOpen = false;
      state.aml = undefined;
      state.warnings = undefined;
      state.blacklistDataVisible = false;
      state.blacklistData = undefined;
      state.senderCompanyOfficials = undefined;
      state.recipientCompanyOfficials = undefined;
      state.warningsInfo = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = nextTransaction;
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = nextTransaction;
          }
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalAml.matchFulfilled,
        (state, action: PayloadAction<Response<TransactionAML>>) => {
          const prevAml = state.aml;
          const nextAml =
            action.payload.data.edges.map((item) => item.node)?.[0] ||
            undefined;

          if (!prevAml) {
            state.aml = nextAml;
          } else if (!isEqual(prevAml, nextAml)) {
            state.aml = nextAml;
          }
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalTransactionWarnings
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionWarning>>) => {
          const prevWarnings = state.warnings;
          const nextWarnings = action.payload.data.edges.map(
            (item) => item.node,
          );

          if (!prevWarnings) {
            state.warnings = nextWarnings;
          } else if (!isEqual(prevWarnings, nextWarnings)) {
            state.warnings = nextWarnings?.map((item) => {
              const result = { ...item };

              const currentPrevWarning = prevWarnings?.find(
                (prevWarning) => prevWarning?.id === result.id,
              );

              if (
                currentPrevWarning &&
                (currentPrevWarning?.blacklistDoc ||
                  currentPrevWarning?.blacklistItem)
              ) {
                result.blacklistDoc = currentPrevWarning.blacklistDoc;
                result.blacklistItem = currentPrevWarning.blacklistItem;
              }

              if (
                currentPrevWarning &&
                currentPrevWarning.relatedTransactions
              ) {
                result.relatedTransactions =
                  currentPrevWarning.relatedTransactions;
              }

              return result;
            });
          }
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalTransactionWarning
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: TransactionWarning }> & {
            meta: {
              arg: {
                originalArgs: {
                  warningId: string;
                };
              };
            };
          },
        ) => {
          const nextWarnings = state.warnings?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningId) {
              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.BLACKLIST
              ) {
                result.blacklistDoc = action.payload.data.blacklistDoc;
                result.blacklistItem = action.payload.data.blacklistItem;
              }

              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.MONITORING
              ) {
                result.relatedTransactions =
                  action.payload.data.relatedTransactions;
              }
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints
          .applyInternalTransactionWarningAction.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionWarning }>) => {
          const nextWarnings = state.warnings?.map((item) => {
            let result = { ...item };

            if (action.payload.data.id === result.id) {
              result = {
                ...action.payload.data,
                blacklistDoc: result.blacklistDoc,
                blacklistItem: result.blacklistItem,
                relatedTransactions: result.relatedTransactions || [],
              };
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        statusesTransactionsApi.endpoints.changeTransactionStatus
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          if (state.transaction) {
            const nextTransaction = { ...state.transaction };

            nextTransaction.baseellaStatus = action.payload.data.baseellaStatus;
            nextTransaction.latestQueuedStatuses = action.payload.data
              .latestQueuedStatuses
              ? action.payload.data.latestQueuedStatuses
              : nextTransaction.latestQueuedStatuses;

            state.transaction = nextTransaction;
          }
        },
      );
    builder.addMatcher(
      internalTransactionsListApi.endpoints.getSenderClientsOfficias
        .matchFulfilled,
      (state, action: PayloadAction<Response<CofProps>>) => {
        state.senderCompanyOfficials = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      internalTransactionsListApi.endpoints.getInternalTransactionWarningsInfo
        .matchFulfilled,
      (state, action: PayloadAction<{ data: WarningsInfo }>) => {
        state.warningsInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      internalTransactionsListApi.endpoints.getRecipientClientsOfficias
        .matchFulfilled,
      (state, action: PayloadAction<Response<CofProps>>) => {
        state.recipientCompanyOfficials = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        if (state.transaction) {
          state.glTransactions = action.payload.data.edges.map(
            (item) => item.node,
          );
        }
      },
    );
  },
});

export const {
  changePanelState,
  clearInternalTransactionState,
  setWarnings,
  setBlacklistData,
  setBlacklistDataVisible,
  setApplyTransactionsWarningIsLoading,
} = internalTransactionSlice.actions;

export default internalTransactionSlice.reducer;
